import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../src/taskpane/redux";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { CopilotLogo } from "../../../src/taskpane/components/copilot/CopilotLogo";
import { addCopilotFiles, openCopilotPane } from "../../../src/taskpane/redux/modules/copilot";
import { ILibraryPlace, libraryRowFileRef, splitReadable } from "../../../src/taskpane/services/copilot/copilotFileRefs";
import { isCopilotReadable } from "../../../src/taskpane/services/copilot/copilotScope";
import { useContentManagerCopilotEnabled } from "../../Copilot/useContentManagerCopilot";

/**
 * "Ask Copilot" in the library grid (spec 2026-10-22 §2.5): while Copilot is on, outside a SharePoint
 * list, for a selection with at least one item Copilot can read (folders and document sets count).
 * The click adds the readable items (the slice keeps what fits; the rest is left out and counted),
 * opens the pane and asks for the Selected files segment; another client's items replace the chosen
 * files, with Keep them too (amended 2026-10-30, §2.13). Refs come from the rows: no network (§2.11).
 * Visibility stops at the first readable row, so a large selection is only converted on the click.
 */
export function useMenuItem_AskCopilot(selectedItems: any[], place: ILibraryPlace | null): { menuItem_AskCopilot: IContextualMenuItem | null } {
  const languageStrings = React.useContext(LanguageContext);
  const dispatch = useDispatch();
  // Only whether Copilot is on: the pane's layout hook listens to every window resize, and the grid
  // holding this would re-render with each one (audit 2026-10-22).
  const { enabled } = useContentManagerCopilotEnabled();
  const isInList: boolean = useSelector((state: RootState) => state.workingDirectories.workingDirectories.some(directory => directory?.type === ComponentTypes.SharePointList));

  const menuItem_AskCopilot = React.useMemo(() => {
    if (!enabled || isInList || !place || !(selectedItems?.length > 0)) {
      return null;
    }
    const hasReadable: boolean = selectedItems.some(row => {
      const ref = libraryRowFileRef(row, place);
      return !!ref && isCopilotReadable(ref);
    });
    if (!hasReadable) {
      return null;
    }
    return {
      key: "askCopilot",
      iconProps: {},
      onRenderIcon: () => <CopilotLogo size={20} className={menuItemIconStyles} />,
      text: languageStrings.CopilotAskCopilot,
      onClick: () => {
        const { files, leftOut } = splitReadable(selectedItems.map(row => libraryRowFileRef(row, place)));
        dispatch(addCopilotFiles(files, { select: true, leftOut, mode: "replaceAnotherClient" }));
        dispatch(openCopilotPane());
      }
    } as IContextualMenuItem;
  }, [enabled, isInList, place, selectedItems, languageStrings, dispatch]);

  return {
    menuItem_AskCopilot
  };
}
